import { useCallback, useMemo } from 'react';
import { telemetry } from '../../../../services/backend';
import { WeatherLocationFavouriteSource } from '../helpers/analytics/weatherLocationFavouriteAnalytics';
import {
  buildAddWeatherLocationFavouritePayload,
  findFavouriteForLocation,
} from '../helpers/location/favouriteIdentity';
import { useWeatherLocationFavourites } from './useWeatherLocationFavourites';

interface UseWeatherLocationFavouriteToggleParams {
  name?: string;
  latitude?: number | string;
  longitude?: number | string;
  state?: string | null;
  region?: string | null;
  locationDescription?: string | null;
  markerType?: string | null;
  markerId?: string | null;
  placeId?: string | null;
  analyticsSource?: WeatherLocationFavouriteSource;
}

export const useWeatherLocationFavouriteToggle = ({
  name,
  latitude,
  longitude,
  state,
  region,
  locationDescription,
  markerType,
  markerId,
  placeId,
  analyticsSource = 'weather page',
}: UseWeatherLocationFavouriteToggleParams) => {
  const { favourites, isLoading, saveFavourite, removeFavourite } =
    useWeatherLocationFavourites();

  const parsedLatitude =
    latitude != null && latitude !== '' ? +latitude : undefined;
  const parsedLongitude =
    longitude != null && longitude !== '' ? +longitude : undefined;

  const matchingFavourite = useMemo(() => {
    if (parsedLatitude == null || parsedLongitude == null) {
      return undefined;
    }

    return findFavouriteForLocation(favourites, {
      markerId,
      placeId,
      latitude: parsedLatitude,
      longitude: parsedLongitude,
    });
  }, [favourites, markerId, parsedLatitude, parsedLongitude, placeId]);

  const isFavourited = Boolean(matchingFavourite);

  const toggleFavourite = useCallback(async () => {
    if (!name || parsedLatitude == null || parsedLongitude == null) {
      return;
    }

    try {
      if (matchingFavourite?.id) {
        await removeFavourite(String(matchingFavourite.id), {
          source: analyticsSource,
        });
        return;
      }

      await saveFavourite(
        buildAddWeatherLocationFavouritePayload({
          name,
          latitude: parsedLatitude,
          longitude: parsedLongitude,
          state,
          region,
          locationDescription,
          markerType,
          markerId,
          placeId,
        }),
        { source: analyticsSource },
      );
    } catch (error) {
      telemetry(
        telemetry.ERROR,
        `[useWeatherLocationFavouriteToggle.toggleFavourite] ${JSON.stringify(error)}`,
      );
    }
  }, [
    analyticsSource,
    locationDescription,
    markerId,
    markerType,
    matchingFavourite?.id,
    name,
    parsedLatitude,
    parsedLongitude,
    placeId,
    region,
    removeFavourite,
    saveFavourite,
    state,
  ]);

  return {
    favourites,
    isFavourited,
    isLoading,
    toggleFavourite,
  };
};
